import type { MasteryRow } from "../lib/api.js";
import { moduleLabel, skillFamilyShort } from "../lib/labels.js";

/**
 * Plain-English summary of the user's skill map. Lives next to the
 * SkillRadar on /home; gives students who can't read the chart at a
 * glance an immediate readable summary.
 *
 * Voice rules: no jargon, no fake compliments, no exclamations. Just
 * state what's strongest, what's weakest, and what to do next. If
 * coverage is too thin (e.g., one or two skills) we say so honestly.
 */
export function SkillSummary({ mastery }: { mastery: MasteryRow[] }) {
  const eligible = mastery.filter((m) => m.attempts >= 3);
  if (eligible.length < 2) {
    return (
      <p
        style={{
          color: "var(--fg-2)",
          fontSize: "var(--t-small)",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        Practice a few more questions across different skills to fill in
        the map. We need at least three attempts per skill before the
        summary becomes useful.
      </p>
    );
  }

  const sorted = [...eligible].sort((a, b) => {
    const rateA = a.attempts > 0 ? a.correct / a.attempts : 0;
    const rateB = b.attempts > 0 ? b.correct / b.attempts : 0;
    return rateB - rateA;
  });

  const strongest = sorted[0]!;
  const weakest = sorted[sorted.length - 1]!;
  const strongPct = Math.round((strongest.correct / strongest.attempts) * 100);
  const weakPct = Math.round((weakest.correct / weakest.attempts) * 100);

  const sameSkill = strongest.skill_family === weakest.skill_family;
  const spread = strongPct - weakPct;

  // If everything looks roughly the same, surface that rather than
  // forcing a fake "weakest" call-out.
  if (sameSkill || spread < 10) {
    return (
      <p
        style={{
          color: "var(--fg-1)",
          fontSize: "var(--t-small)",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        Your accuracy is consistent across {eligible.length} skill
        {eligible.length === 1 ? "" : "s"} (around {Math.round((strongPct + weakPct) / 2)}%).
        Try a session in a different module to spread the picture.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <p
        style={{
          color: "var(--fg-1)",
          fontSize: "var(--t-small)",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        <strong style={{ color: "var(--fg-0)", fontWeight: 600 }}>
          Strongest:
        </strong>{" "}
        {skillFamilyShort(strongest.skill_family)} ({moduleLabel(strongest.module)}) at{" "}
        <strong style={{ color: "var(--signal-green)", fontWeight: 600 }}>
          {strongPct}%
        </strong>
        .
      </p>
      <p
        style={{
          color: "var(--fg-1)",
          fontSize: "var(--t-small)",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        <strong style={{ color: "var(--fg-0)", fontWeight: 600 }}>
          Focus next:
        </strong>{" "}
        {skillFamilyShort(weakest.skill_family)} ({moduleLabel(weakest.module)}) at{" "}
        <strong style={{ color: "var(--accent-amber)", fontWeight: 600 }}>
          {weakPct}%
        </strong>
        . That gap of {spread} points has the most room to gain.
      </p>
    </div>
  );
}
